import { FormControl, InputLabel, MenuItem, Select, SelectProps, Tooltip } from "@mui/material";
import { FieldPath, FieldValues, useController } from "react-hook-form";
import { selectDefaultProps } from "../Forms/selectDefaultProps";
import { Country } from "./Country";
import { useFetchCountries } from "./useFetchCountries";
import { RhfInputProps } from "../Forms/RhfInputProps";
import { emptyMenuItem } from "../Forms/SelectDefaultComponents";
import { RHFFieldErrorText } from "../Forms/RHFFieldErrorText";

interface CountrySelectProps {
  onCountryChanged?: (countryId: number, countries: Country[]) => void;
  hideLabel?: boolean;
  showErrorInTooltip?: boolean;
}

export function CountrySelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  onCountryChanged,
  hideLabel,
  showErrorInTooltip,
  required,
  ...otherProps
}: RhfInputProps<TFieldValues, TName> & CountrySelectProps & SelectProps) {
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const { data: countries } = useFetchCountries();
  return (
    <Tooltip title={showErrorInTooltip ? error?.message : undefined} arrow>
      <FormControl fullWidth size="small" error={!!error} required={required}>
        {!hideLabel && <InputLabel id="country-label">Country</InputLabel>}
        <Select
          {...field}
          {...otherProps}
          {...selectDefaultProps}
          labelId="country-label"
          label={!hideLabel ? "Country" : undefined}
          onChange={(event) => {
            if (onCountryChanged) onCountryChanged(event.target.value as number, countries || []);
            field.onChange(event);
          }}
          required={required}
          error={!!error}
        >
          {emptyMenuItem(0)}
          {countries &&
            countries.map((country) => (
              <MenuItem key={country.Id} value={country.Id}>
                {country.Name}
              </MenuItem>
            ))}
        </Select>
        {!showErrorInTooltip && !!error && <RHFFieldErrorText fieldError={error} />}
      </FormControl>
    </Tooltip>
  );
}
